import { Injectable, computed, signal } from '@angular/core';
import { Observable, delay, map, of } from 'rxjs';
import { SAVED_TENDERS_MOCK, TENDERS_MOCK } from '../mocks/tenders.mock';
import { SavedTender, Tender, TenderSummary } from '../models/tender.model';

const LATENCY_MS = 250;

/**
 * Mock implementation. The public methods return Observables so they can later be
 * replaced by HttpClient calls without touching the components.
 */
@Injectable({ providedIn: 'root' })
export class TenderService {
  private readonly savedState = signal<SavedTender[]>(structuredClone(SAVED_TENDERS_MOCK));

  readonly saved = this.savedState.asReadonly();
  readonly savedCount = computed(() => this.savedState().filter((s) => !s.archived).length);

  list(): Observable<TenderSummary[]> {
    return of(TENDERS_MOCK).pipe(delay(LATENCY_MS));
  }

  get(id: string): Observable<Tender | undefined> {
    return of(TENDERS_MOCK).pipe(
      delay(LATENCY_MS),
      map((all) => all.find((t) => t.id === id)),
    );
  }

  isSaved(id: string): boolean {
    return this.savedState().some((s) => s.tenderId === id);
  }

  toggleSave(id: string): void {
    this.savedState.update((list) =>
      list.some((s) => s.tenderId === id)
        ? list.filter((s) => s.tenderId !== id)
        : [{ tenderId: id, savedAt: new Date().toISOString().slice(0, 10), archived: false }, ...list],
    );
  }

  setArchived(id: string, archived: boolean): void {
    this.savedState.update((list) => list.map((s) => (s.tenderId === id ? { ...s, archived } : s)));
  }
}
